import * as Preact from 'preact';
import * as Hooks from 'preact/hooks';
import { useParentAPI } from './hooks/useParentAPI';

import Helmet from 'preact-helmet';
import { ThemeProvider } from '@mui/material/styles';
import { usePlatformMediaQuery, usePortraitMediaQuery } from './hooks/mediaQueries';
import * as Scene from './sceneView';
import * as Inspiration from './inspiration';
import * as Configuration from './configuration';
import TagManager from 'react-gtm-module';
import { useEffect } from 'preact/hooks';
import { useDispatch, useSelector } from './hooks/redux';
import * as Selectors from '../store/selectors';
import * as Error from './appError';
import { initialiseTracking } from '../store/middleware/track';
import { AppControlContext } from './context/appControl';
import { themes } from '@dora/components';
import * as i18n from '../i18n';
import { TranslateProvider } from '@denysvuika/preact-translate';
import { useAddCommand } from './hooks/useConsoleCommand';
import * as CoreStore from '../store/core';
import ErrorBoundary from './configuration/errorBoundary';
import { AppError } from '../data/appError';
import { isLogLevelName, LogLevel } from '@dora/lumberjack';
import { setLogLevel } from '../store/middleware/log';

export function Root(_props: {}) {
    const {
        view,
        currentUrl,
        closeApp,
        enhanceCloseApp,
        proceedToCart,
        trackEvent,
        toggleModal,
        updateConversionId,
    } = useParentAPI();

    const logLevel = useSelector(Selectors.Core.logLevel);

    useEffect(() => {
        setLogLevel(logLevel);
    }, [logLevel])

    const locale = useSelector(Selectors.Core.locale);
    const localisationDictionary = i18n.localisationDictionary;

    const gtmArgs = useSelector(Selectors.Core.gtm);
    const environment = useSelector(Selectors.Core.environment);
    const appError = useSelector(Selectors.Core.error);
    const conversionId = useSelector(Selectors.ConversionId.conversionId);
    const tenant = useSelector(Selectors.Core.tenantIdentifier);

    conversionId && updateConversionId(conversionId);
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);
    initialiseTracking(trackEvent);

    useEffect(() => {
        if (gtmArgs !== undefined) {
            const tagManagerArgs = gtmArgs;
            TagManager.initialize(tagManagerArgs);
        }
    }, [gtmArgs]);

    const theme = themes.getTheme(tenant);
    const platform = usePlatformMediaQuery(theme);
    const isPortrait = usePortraitMediaQuery();

    const appControl = Hooks.useMemo(
        () => ({ closeApp, enhanceCloseApp }),
        [closeApp, enhanceCloseApp],
    );

    const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

    Hooks.useEffect(
        () =>
            // outside of the logging system so it can't be turned off by an environment variable
            console.log(
                '\n Powered By Fixtuur Visual Commerce \n \n https://www.fixtuur.com/ \n \n',
            ),
        [],
    );

    // Command for disabling error handling that would take over the whole page, so the app can still be
    // used with broken or incomplete product data while development is ongoing
    const [disableDataErrorHandling, setDisableDataErrorHandling] = Hooks.useState(false);
    const dispatch = useDispatch();
    useAddCommand(
        'disableDataErrorHandling',
        () => {
            // Ensure any existing errors are removed
            dispatch(CoreStore.clearError());
            setDisableDataErrorHandling(true);
        },
        [],
    );

    return (
        <>
            <Helmet
                link={[
                    {
                        rel: 'stylesheet',
                        href: 'https://fonts.googleapis.com/css?family=Inter:300,400,500,600,700&display=swap',
                    },
                ]}
                meta={[
                    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
                    { name: 'og:title', content: 'Moda 3D Outdoor Space Planner' },
                    { name: 'og:description', content: 'Check out my Moda outdoor space' },
                    { name: 'og:image', content: `assets/${tenantIdentifier}/share.png` },
                    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
                ]}
                style={[
                    {
                        type: 'text/css',
                        cssText: 'body {height: 100vh; margin: 0; overflow: hidden}',
                    },
                ]}
            />
            {locale && (
                <TranslateProvider lang={locale} translations={localisationDictionary}>
                    <AppControlContext.Provider value={appControl}>
                        <ThemeProvider theme={theme}>
                            {(() => {
                                /** an app-level error should supercede all other views */
                                if (appError !== undefined) {
                                    return <Error.Component error={appError} />;
                                }
                                switch (view) {
                                    case 'scene':
                                        if (proceedToCart !== undefined) {
                                            return (
                                                <Scene.Component
                                                    environment={environment ?? 'production'}
                                                    platform={platform}
                                                    portrait={isPortrait}
                                                    proceedToCart={proceedToCart}
                                                    copyToClipboard={copyToClipboard}
                                                    currentUrl={currentUrl}
                                                    toggleModal={toggleModal}
                                                />
                                            );
                                        } else {
                                            dispatch(
                                                CoreStore.error(AppError.ADD_TO_CART_UNDEFINED),
                                            );
                                        }
                                    case 'inspiration_runway':
                                        return (
                                            <Inspiration.Component
                                                platform={platform}
                                                proceedToCart={proceedToCart}
                                            />
                                        );
                                    case 'configuration':
                                        if (proceedToCart !== undefined) {
                                            return (
                                                <ErrorBoundary>
                                                    <Configuration.Component
                                                        proceedToCart={proceedToCart}
                                                        platform={platform}
                                                        portrait={isPortrait}
                                                        copyToClipboard={copyToClipboard}
                                                        toggleModal={toggleModal}
                                                        disableDataErrorHandling={
                                                            disableDataErrorHandling
                                                        }
                                                    />
                                                </ErrorBoundary>
                                            );
                                        } else {
                                            dispatch(
                                                CoreStore.error(AppError.ADD_TO_CART_UNDEFINED),
                                            );
                                        }
                                    // Returning null to prevent flashes of the start view
                                    case 'start':
                                    default:
                                        return null;
                                }
                            })()}
                        </ThemeProvider>
                    </AppControlContext.Provider>
                </TranslateProvider>
            )}
        </>
    );
}
